import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ForumPost, ForumCategory as ForumCategoryType, ForumPostFilters } from '../types';
import { apiClient } from '../services/apiClient';
import { LoadingSpinner } from './LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { getRoleDisplay, canModerate } from '../utils/roles';
import { getMedicalRoleLabel } from '../utils/medicalRoles';

export function ForumCategory() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [category, setCategory] = useState<ForumCategoryType | null>(null);
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Parse search params
  const filters: ForumPostFilters = useMemo(() => ({
    category_id: categoryId,
    search: searchParams.get('search') || '',
    sort_by: (searchParams.get('sort_by') as any) || 'created_at',
    sort_order: (searchParams.get('sort_order') as any) || 'desc',
    page: parseInt(searchParams.get('page') || '1'),
    limit: parseInt(searchParams.get('limit') || '20')
  }), [categoryId, searchParams]);

  useEffect(() => {
    if (categoryId) {
      fetchCategory();
      fetchPosts();
    }
  }, [categoryId, filters]);

  const fetchCategory = async () => {
    try {
      const response = await apiClient.get('/forum?path=categories');
      if (response.data.success) {
        const categories = response.data.data;
        const currentCategory = categories.find((cat: ForumCategoryType) => cat.id === categoryId);
        setCategory(currentCategory || null);
      }
    } catch (err) {
      console.error('Error fetching category:', err);
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await apiClient.get(`/forum?path=posts&${params.toString()}`);
      
      if (response.data.success) {
        setPosts(response.data.data.posts);
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalCount(response.data.data.pagination.totalCount);
      } else {
        setError(response.data.message);
      }
    } catch (err: any) {
      console.error('Error fetching posts:', err);
      setError('Σφάλμα κατά τη φόρτωση των δημοσιεύσεων');
    } finally {
      setLoading(false);
    }
  };

  const updateSearchParams = (newParams: Partial<ForumPostFilters>) => {
    const updatedParams = new URLSearchParams(searchParams);
    
    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        updatedParams.set(key, value.toString());
      } else {
        updatedParams.delete(key);
      }
    });

    // Reset to page 1 when changing filters
    if (Object.keys(newParams).some(key => key !== 'page')) {
      updatedParams.set('page', '1');
    }

    setSearchParams(updatedParams);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const search = formData.get('search') as string;
    updateSearchParams({ search });
  };

  const handleSortChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    updateSearchParams({ sort_by: sortBy as any, sort_order: sortOrder });
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    const weeks = Math.floor(diff / 604800000);
    
    if (minutes < 60) {
      return `${minutes} λεπτά πριν`;
    } else if (hours < 24) {
      return `${hours} ώρες πριν`;
    } else if (days < 7) {
      return `${days} μέρες πριν`;
    } else if (weeks < 4) {
      return `${weeks} εβδομάδες πριν`;
    } else {
      return date.toLocaleDateString('el-GR');
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const currentPage = filters.page || 1;
    const pages = [];
    
    // Add previous page
    if (currentPage > 1) {
      pages.push(
        <button
          key="prev"
          onClick={() => updateSearchParams({ page: currentPage - 1 })}
          className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded"
        >
          Προηγούμενη
        </button>
      );
    }

    // Add page numbers
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => updateSearchParams({ page: i })}
          className={`px-3 py-2 text-sm rounded ${
            i === currentPage
              ? 'bg-blue-600 text-white'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          {i}
        </button>
      );
    }

    // Add next page
    if (currentPage < totalPages) {
      pages.push(
        <button
          key="next"
          onClick={() => updateSearchParams({ page: currentPage + 1 })}
          className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded"
        >
          Επόμενη
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center space-x-2 mt-8">
        {pages}
      </div>
    );
  };

  if (!categoryId) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Δεν βρέθηκε η κατηγορία</p>
        <Link to="/forum" className="text-blue-600 hover:text-blue-800">
          Επιστροφή στο φόρουμ
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/forum" className="text-gray-700 hover:text-blue-600">
              Φόρουμ
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
              </svg>
              <span className="ml-1 md:ml-2 text-gray-500">
                {category?.name || 'Φόρτωση...'}
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Category Header */}
      {category && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div 
                className="flex-shrink-0 w-16 h-16 rounded-lg flex items-center justify-center text-white text-2xl"
                style={{ backgroundColor: category.color || '#3B82F6' }}
              >
                {category.icon ? (
                  <i className={category.icon}></i>
                ) : (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.418 8-9 8a9.013 9.013 0 01-5.314-1.673l-3.563.789a1.5 1.5 0 01-1.777-1.777l.789-3.563A8.963 8.963 0 013 12C3 7.582 7.582 3 12 3s9 3.582 9 9z" />
                  </svg>
                )}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{category.name}</h1>
                {category.description && (
                  <p className="mt-2 text-gray-600">{category.description}</p>
                )}
                <div className="mt-2 text-sm text-gray-500">
                  {totalCount} {totalCount === 1 ? 'δημοσίευση' : 'δημοσιεύσεις'}
                </div>
              </div>
            </div>

            {user && (
              <Link
                to={`/forum/create?category=${categoryId}`}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Νέα Δημοσίευση
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <input
                type="text"
                name="search"
                placeholder="Αναζήτηση δημοσιεύσεων..."
                defaultValue={filters.search}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <button
                type="submit"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <span className="sr-only">Αναζήτηση</span>
              </button>
            </div>
          </form>

          {/* Sort Options */}
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">Ταξινόμηση:</span>
            <select
              value={`${filters.sort_by}_${filters.sort_order}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('_');
                handleSortChange(sortBy, sortOrder as 'asc' | 'desc');
              }}
              className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="created_at_desc">Νεότερα πρώτα</option>
              <option value="created_at_asc">Παλαιότερα πρώτα</option>
              <option value="reply_count_desc">Περισσότερες απαντήσεις</option>
              <option value="view_count_desc">Περισσότερες προβολές</option>
              <option value="title_asc">Αλφαβητικά (Α-Ω)</option>
              <option value="title_desc">Αλφαβητικά (Ω-Α)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Posts List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800 mb-4">{error}</p>
          <button
            onClick={fetchPosts}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Δοκιμάστε ξανά
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {posts.length > 0 ? (
            <>
              <div className="divide-y divide-gray-200">
                {posts.map((post) => (
                  <div key={post.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {/* Post Title and Status */}
                        <div className="flex items-center space-x-2 mb-2">
                          {post.is_pinned && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                              </svg>
                              Καρφιτσωμένη
                            </span>
                          )}
                          {post.is_locked && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                              </svg>
                              Κλειδωμένη
                            </span>
                          )}
                        </div>

                        <Link
                          to={`/forum/post/${post.id}`}
                          className="text-xl font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                        >
                          {post.title}
                        </Link>

                        {/* Post Meta */}
                        <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {post.author_name}
                            {post.author_medical_role && (
                              <span className="ml-1 text-xs">
                                {getMedicalRoleLabel(post.author_medical_role, post.author_sex)}
                              </span>
                            )}
                          </span>
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {formatTimeAgo(post.created_at)}
                          </span>
                        </div>

                        {/* Post Preview */}
                        <p className="mt-3 text-gray-600 line-clamp-2">
                          {post.content.replace(/<[^>]*>/g, '').substring(0, 200)}
                          {post.content.length > 200 ? '...' : ''}
                        </p>

                        {/* Last Reply Info */}
                        {post.last_reply_author && post.last_reply_created_at && (
                          <div className="mt-3 text-sm text-gray-500">
                            Τελευταία απάντηση από{' '}
                            <span className="font-medium">{post.last_reply_author}</span>{' '}
                            {formatTimeAgo(post.last_reply_created_at)}
                          </div>
                        )}
                      </div>

                      {/* Post Stats */}
                      <div className="flex-shrink-0 ml-6 text-center">
                        <div className="text-2xl font-bold text-gray-900">{post.reply_count}</div>
                        <div className="text-sm text-gray-500">
                          {post.reply_count === 1 ? 'απάντηση' : 'απαντήσεις'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {post.view_count} προβολές
                        </div>
                      </div>
                    </div>

                    {/* Moderator Actions */}
                    {user && canModerate(user.role) && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Ενέργειες:</span>
                          <Link
                            to={`/forum/post/${post.id}`}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Διαχείριση
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {renderPagination()}
            </>
          ) : (
            <div className="px-6 py-12 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.418 8-9 8a9.013 9.013 0 01-5.314-1.673l-3.563.789a1.5 1.5 0 01-1.777-1.777l.789-3.563A8.963 8.963 0 013 12C3 7.582 7.582 3 12 3s9 3.582 9 9z" />
              </svg>
              <p className="mt-4 text-lg">Δεν υπάρχουν δημοσιεύσεις σε αυτή την κατηγορία</p>
              <p className="text-sm">
                {user ? (
                  <>
                    Γίνετε ο πρώτος που θα δημοσιεύσει!{' '}
                    <Link 
                      to={`/forum/create?category=${categoryId}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Δημιουργήστε μια δημοσίευση
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="text-blue-600 hover:text-blue-800">
                      Συνδεθείτε
                    </Link>{' '}
                    για να δημιουργήσετε μια δημοσίευση
                  </>
                )}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}